import { useCallback, useState } from 'react'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import {
  DROP_CAP_DEFAULT_LINES,
  DROP_CAP_DEFAULT_OFFSET,
  DROP_CAP_LINES_MIN,
  DROP_CAP_LINES_RANGE_MESSAGE,
  DROP_CAP_OFFSET_MIN,
  DROP_CAP_OFFSET_RANGE_MESSAGE,
  type DropCapOffsetPresetName,
  computeDropCapPresetOffset,
  matchDropCapOffsetPreset,
  validateDropCapLinesInput,
  validateDropCapOffsetInput,
} from '@/lib/text/drop-cap'
import type { DropCapStyle } from '@/lib/text/types/drop-cap-style'
import type { DropCapSelectionSummary } from '@/lib/text/types/drop-cap-selection-summary'
import { useDropCapNumberField } from './use-drop-cap-number-field'

const toLinesUpdate = (value: number): TextRunStyleUpdate => ({ dropCapLines: value })
const toOffsetUpdate = (value: number): TextRunStyleUpdate => ({ dropCapOffset: value })

interface DropCapOptions {
  disabled: boolean
  isSelectedShapeTextFrame: boolean
  fontSizeValue: number
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
  restoreActiveEditor: () => void
}

/** Drop Cap: the on/off switch, Inside/Outside style, offset presets, and Lines/Offset fields. */
export function useDropCap({
  disabled,
  isSelectedShapeTextFrame,
  fontSizeValue,
  emitStyleUpdate,
  restoreActiveEditor,
}: DropCapOptions) {
  // Drop Cap. `dropCapStyle` is null only while the switch itself is off — a single-select toggle,
  // once Drop Cap is on, always has exactly one of Inside/Outside pressed; re-clicking the already-
  // active style is a no-op rather than a way to clear it (see `handleIconClick`).
  const [dropCap, setDropCap] = useState(false)
  // Not rendered anywhere (no midpoint/label — see `applyDropCapSummary`'s own comment): tracks
  // whether EVERY paragraph in the selection actually has an active drop cap (`hasDropCap === true`
  // ), as opposed to `dropCap` above, which is also true for a partial mix. `dropCapStyle` is scoped
  // to the on-paragraphs alone (`summarizeDropCapSelection`), so it can read as a uniform style
  // (e.g. 'INS') even when an off paragraph is still in the mix — `handleIconClick`'s
  // already-active no-op guard needs this extra bit to tell "everyone already matches" apart from
  // "the on ones happen to match, but an off paragraph doesn't have this style yet".
  const [dropCapUniform, setDropCapUniform] = useState(false)
  const [dropCapStyle, setDropCapStyle] = useState<DropCapStyle | null>(null)

  const lines = useDropCapNumberField({
    defaultValue: DROP_CAP_DEFAULT_LINES,
    displayFloor: DROP_CAP_LINES_MIN,
    validate: validateDropCapLinesInput,
    rangeMessage: DROP_CAP_LINES_RANGE_MESSAGE,
    toUpdate: toLinesUpdate,
    dropCap,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const offset = useDropCapNumberField({
    defaultValue: DROP_CAP_DEFAULT_OFFSET,
    displayFloor: DROP_CAP_OFFSET_MIN,
    validate: validateDropCapOffsetInput,
    rangeMessage: DROP_CAP_OFFSET_RANGE_MESSAGE,
    toUpdate: toOffsetUpdate,
    dropCap,
    emitStyleUpdate,
    restoreActiveEditor,
  })
  const { applySummary: applyLinesSummary, reset: resetLines } = lines
  const { applySummary: applyOffsetSummary, reset: resetOffset } = offset

  // Drop Cap only makes sense on a genuine text frame — unlike the other controls, which stay
  // enabled for any text-capable shape so a user can pre-set style before typing, Drop Cap has
  // nothing to act on for a plain shape or a photo. See `isSelectedShapeTextFrame` doc comment.
  const dropCapControlsDisabled = disabled || !isSelectedShapeTextFrame
  const dropCapSubControlsDisabled = dropCapControlsDisabled || !dropCap

  const activeDropCapOffsetPreset = offset.mixed
    ? null
    : matchDropCapOffsetPreset(offset.value[0], fontSizeValue)

  /** Switch toggled on: activate the default style (Inside) if none was active before, keeping
   *  whatever style was last chosen if one was. Toggled off: remove the style entirely, but leave
   *  the Lines/Offset WIRE values untouched — they're simply inert while `dropCap === 'NONE'`, which
   *  is what lets reactivating show the paragraph's own last-used values rather than always
   *  resetting to the 1/1 default.
   *
   *  A disabled panel must not go on showing a validation error, though (a validation error only
   *  ever makes sense while the field is editable) — so an in-progress invalid edit is reverted back
   *  to the last committed value here unconditionally, regardless of what's wrong with it. This is
   *  broader than what blurring the field does on its own (see the fields' `handleBlur`, which
   *  only silently revert an EMPTY field — a genuinely out-of-range or unparseable non-empty value
   *  is left in its error state so the user can still see and fix it, per the Drop Cap
   *  Error/Invalid designs), but turning the switch off hides the field entirely, so there's no
   *  error left to see or fix until it's turned back on. Without this, switching off mid-edit of an
   *  invalid value left the error message showing under a now-disabled field, and switching back on
   *  showed the stale invalid text with no error to explain it. */
  const handleSwitchChange = (checked: boolean) => {
    setDropCap(checked)
    // Applies to every paragraph in the selection either way (`emitStyleUpdate` below), so the
    // selection is genuinely uniform afterwards regardless of what it was before.
    setDropCapUniform(checked)

    if (checked) {
      const nextStyle = dropCapStyle ?? 'INS'

      setDropCapStyle(nextStyle)
      emitStyleUpdate({ dropCap: nextStyle })
    } else {
      setDropCapStyle(null)
      emitStyleUpdate({ dropCap: 'NONE' })
      // Also drop the "unsynced edit pending" guard the fields' own onBlur handlers may have left
      // set (see the comment there) — the panel is going disabled, so there's nothing left to
      // protect from the read-model sync effect; leaving it set would block that effect from
      // picking up a legitimate later change to the paragraph's own Lines/Offset while disabled.
      lines.revertPendingEdit()
      offset.revertPendingEdit()
    }

    restoreActiveEditor()
  }

  /** A single-select toggle: clicking the OTHER style switches mode, preserving Lines/Offset.
   *  Clicking the already-active style is a no-op — it stays pressed and Drop Cap stays active with
   *  that same style. The switch itself is the only way to turn Drop Cap off entirely.
   *
   *  The no-op guard requires `dropCapUniform` in addition to `dropCapStyle === style` — `style` is
   *  scoped to the paragraphs that already have a drop cap (`summarizeDropCapSelection`), so it can
   *  already read as 'INS' even while an off paragraph is still mixed into the selection. Without
   *  `dropCapUniform`, clicking Inside in that state would incorrectly no-op instead of extending
   *  the style to the off paragraph too — which `emitStyleUpdate` below applies to every paragraph
   *  in the selection regardless. */
  const handleIconClick = (style: DropCapStyle) => {
    if (dropCapUniform && dropCapStyle === style) return

    setDropCapStyle(style)
    setDropCapUniform(true)
    emitStyleUpdate({ dropCap: style })
    restoreActiveEditor()
  }

  const handleOffsetPresetClick = (name: DropCapOffsetPresetName) => {
    offset.commitValue(computeDropCapPresetOffset(name, fontSizeValue))
    restoreActiveEditor()
  }

  // Shared between the editing-mode branch of the selection sync (a real text-tool selection, via
  // `shapeEditStyle.dropCapSummary`) and the whole-frame Select-Tool branch (via
  // `summarizeDropCapSelection(paras)` directly) — both resolve to the same
  // `DropCapSelectionSummary` shape, so both drive the panel identically. `hasDropCap`
  // disagreeing (some paragraphs have a drop cap, some don't) is a distinct signal from `style`
  // disagreeing (Inside vs Outside both represented); both drive their own part of the display.
  const applyDropCapSummary = useCallback(
    (summary: DropCapSelectionSummary) => {
      // Mixed (some paragraphs have a drop cap, some don't) still shows the switch as fully ON,
      // not an indeterminate midpoint — unlike other mixed on/off switches in this panel family
      // (e.g. the Object panel's Ignore Wrap), Drop Cap's Lines/Offset/style controls already have
      // their own explicit "Mixed" affordances below, and turning it on for every paragraph is the
      // same single click either way, so a midpoint + label here would only add a second, redundant
      // "mixed" signal for the same underlying disagreement.
      setDropCap(summary.hasDropCap !== false)
      setDropCapUniform(summary.hasDropCap === true)
      setDropCapStyle(summary.style)

      // `summary.lines`/`.offset` are computed over the drop-cap-bearing paragraphs alone (see
      // `summarizeDropCapSelection`'s own doc comment) — a paragraph with no active drop cap simply
      // isn't counted, the same way it isn't for `style`. But UNLIKE `style`, that's not the whole
      // story here: `hasDropCap === null` (some paragraphs have one, some don't) always shows
      // Lines/Offset as Mixed too, regardless of whether the on-paragraphs numerically agree — an
      // off paragraph reads to the user as having no Lines/Offset setting at all (same as it has no
      // style), not as silently agreeing via a value it never meaningfully had. Only once every
      // paragraph in the selection is uniformly ON does a real numeric agreement mean anything. When
      // `dropCap` is false (no paragraph has one), the fields are disabled and display the shared
      // floor instead (see `displayedValue` in `use-drop-cap-number-field.ts`), so neither of these
      // visibly matters in that case.
      const linesMixedNow =
        summary.hasDropCap === true ? summary.lines === null : summary.hasDropCap === null
      const offsetMixedNow =
        summary.hasDropCap === true ? summary.offset === null : summary.hasDropCap === null

      applyLinesSummary(linesMixedNow, summary.lines)
      applyOffsetSummary(offsetMixedNow, summary.offset)
    },
    [applyLinesSummary, applyOffsetSummary]
  )

  /** Nothing selected — Drop Cap has no paragraph to act on; the section stays disabled via
   *  `dropCapControlsDisabled` regardless of these values, but reset them to a clean baseline. */
  const resetDropCap = useCallback(() => {
    setDropCap(false)
    setDropCapUniform(false)
    setDropCapStyle(null)
    resetLines()
    resetOffset()
  }, [resetLines, resetOffset])

  return {
    dropCap,
    dropCapStyle,
    dropCapControlsDisabled,
    dropCapSubControlsDisabled,
    activeDropCapOffsetPreset,
    lines,
    offset,
    handleSwitchChange,
    handleIconClick,
    handleOffsetPresetClick,
    applyDropCapSummary,
    resetDropCap,
  }
}

export type DropCapControls = ReturnType<typeof useDropCap>
